import styles from './BimQueryPage.module.css';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';

import useBimContext from 'components/hooks/useBimContext';
import BimQueryRightMenu from 'components/bng/pages/admin/structures/bimQuery/BimQueryRightMenu';
import { bngYup } from 'components/bng/form/yup/BngYup';
import BimQuerySqlEditor from 'components/bng/pages/admin/structures/bimQuery/BimQuerySqlEditor';
import UiBlocker from 'components/bng/ui/UiBlocker';
import { Tab, TabSet } from 'components/ui/TabSet';
import AssistedMode from 'components/bng/pages/admin/structures/bimQuery/AssistedMode';
import BngDropdown from 'components/bng/ui/BngDropdown';
import Icon from 'components/ui/common/Icon';
import Api from 'components/Api';
import UiMsg from 'components/ui/UiMsg';
import QueryResultPreview from 'components/bng/pages/admin/structures/bimQuery/menuTabs/QueryResultPreview';
import ColumnsConfiguration from 'components/bng/pages/admin/structures/bimQuery/menuTabs/ColumnsConfiguration';
import LogPreview from 'components/bng/pages/admin/structures/bimQuery/menuTabs/LogPreview';
import bimEventBus from 'BimEventBus';
import BngIconButton from 'components/bng/ui/BngIconButton';
import useTranslation from 'components/hooks/useTranslation';
import Utils from 'components/Utils';
import useAsyncEffect from 'components/hooks/useAsyncEffect';
import useBimQueryPageCtx from 'components/bng/pages/admin/structures/bimQuery/useBimQueryPageCtx';
import { checkAddonEnabled } from 'components/bng/accounts/addons/AddonDisabledDialog';
import AddonType from 'components/bng/accounts/AddonType';
import OpConfirmation from 'components/ui/OpConfirmation';
import useAdaAiChatStore from 'components/bng/adaAi/useAdaAiChatStore';
import UUID from 'bng/utils/UUID';

const buildSchema = (structureNameValidator, validateFieldsConfiguration, t) =>
  bngYup((yup) =>
    yup.object({
      // First step
      structureName: yup.string().max(35).default(''),
      displayName: yup
        .string()
        .matches(/^[^0-9]*$/, t('inMemory_nameContainSpecialChars'))
        .test({
          async test(value) {
            if (_.isEmpty(value)) {
              return false;
            }
            const isInUse = await structureNameValidator(value);
            return isInUse ? this.createError({ message: t('inMemory_nameAlreadyInUse') }) : true;
          },
        })
        .required()
        .max(35)
        .default(''),
      structureIcon: yup.string().required().default('cloud'),
      structureDescription: yup.string().required().default(''),
      structureType: yup.string().default('ANALYTICAL'),
      fullLoads: yup.boolean().default(false),
      structureTag: yup.number().default(0),
      // Second step
      connection: yup
        .mixed()
        .test({
          test(value) {
            return value === 'BIMWarehouse' || value > 0;
          },
        })
        .required()
        .default(0),
      fieldConfigs: yup
        .array(yup.object())
        .test({
          test(value) {
            return validateFieldsConfiguration(value);
          },
        })
        .required()
        .default([]),
      sql: yup.string().required().default(''),
      // Third step
      dataRecyclingEnabled: yup
        .boolean()
        .test({
          test(value) {
            return value ? this.parent.dataRecyclingFieldName !== '' : true;
          },
        })
        .default(false),
      dataRecyclingFieldName: yup.string().default(''),
      dataRecyclingPeriod: yup.number().default(2),
      dataRecyclingType: yup.string().default('YEAR'),
      dimensions: yup.array(yup.object()).default([]),
      descriptionsCase: yup.string().default('UPPER_CASE'),
      descriptionsAccentuation: yup.string().default('KEEP_ACCENTUATION'),
      trim: yup.boolean().default(true),
      trimWords: yup.boolean().default(true),
      dataPartitioningEnabled: yup.boolean().default(false),
      dataPartitioningFieldName: yup.string().default(''),
      dataPartitioningRange: yup.string().default('YEAR'),
      bimQueryMetadata: yup.object({
        schema: yup.string().required(),
        nodes: yup
          .array(
            yup.object({
              id: yup.string().required(),
              position: yup
                .object({
                  x: yup.number().required(),
                  y: yup.number().required(),
                })
                .required(),
              type: yup.string().required().default('databaseTable'),
              data: yup
                .object({
                  tableInfo: yup
                    .object({
                      fields: yup
                        .array(
                          yup.object({
                            name: yup.string().required().default(''),
                            type: yup.string().required().default('TEXT'),
                            selected: yup.boolean().required().default(true),
                            dimensionType: yup.string().nullable().default(''),
                          })
                        )
                        .required(),
                      schema: yup.string().required().default(''),
                      table: yup.string().required().default(''),
                      filters: yup
                        .array(
                          yup.object({
                            condition: yup.string().default(''),
                            fieldName: yup.string().required().default(''),
                            operator: yup.string().nullable().default(''),
                            value: yup.string().required().default(''),
                          })
                        )
                        .default([]),
                    })
                    .required(),
                })
                .required(),
            })
          )
          .default([]),
        edges: yup
          .array(
            yup.object({
              id: yup.string().required(),
              source: yup.string().required().default(''),
              sourceHandle: yup.string(),
              target: yup.string().required().default(''),
              targetHandle: yup.string(),
              type: yup.string(),
            })
          )
          .default([]),
        tableQueries: yup
          .array(
            yup.object({
              id: yup.string().required(),
              query: yup.string().required(),
            })
          )
          .default([]),
        mode: yup.string().required().default('ASSISTED_MODE'),
      }),
    })
  );

export const normalizeFieldDescription = (value = '') => {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export default function BimQueryPage() {
  const { msg, project, labs, user } = useBimContext();
  const $pageCtx = useBimQueryPageCtx();
  const $adaAiChatStore = useAdaAiChatStore();
  const { t } = useTranslation();
  const { id: structureId } = useParams();

  const $runningTimeRef = useRef();
  const $rightMenuFormikRef = useRef();
  const $resizer = useRef();

  const [currentAccordion, setCurrentAccordion] = useState();
  const [queryResult, setQueryResult] = useState([]);
  const [resultLimit, setResultLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [runningTime, setRunningTime] = useState('00:00');
  const [queryExecutionLog, setQueryExecutionLog] = useState(null);
  const [structureToEdit, setStructureToEdit] = useState({});
  const [tabsHeight, setTabsHeight] = useState({
    assistedModeHeight: 0,
    sqlEditorHeight: 5,
    assistedModeBottom: 0,
    sqlEditorBottom: 0,
  });
  const [activeTopTabIndex, setActiveTopTabIndex] = useState(0);
  const [blockEdit, setBlockEdit] = useState(structureId !== undefined);
  const [sourceSql, setSourceSql] = useState('');

  useAsyncEffect({
    onMount: async () => {
      if (!labs.features.some((feature) => feature === 'BIM_QUERY')) {
        window.location.replace(Api.buildUrl('/pages/errors/403.iface'));
        return;
      }

      try {
        await $pageCtx.fetchConnections(project.id);
      } catch (e) {
        console.error('Error while fetching connections', e);
        UiMsg.ajaxError(null, e);
      }

      if (structureId) {
        try {
          setLoading(true);
          const structure = await Api.BimQuery.fetchStructure(structureId);
          setStructureToEdit(structure);
          setSourceSql(structure.sql);
          setActiveTopTabIndex(structure.bimQueryMetadata.mode === 'ASSISTED_MODE' ? 0 : 1);
        } catch (e) {
          console.error('Error on fetchStructure()', e);
          UiMsg.ajaxError(t('bim.query.fetching.structure.infos.error'), e);
        } finally {
          setLoading(false);
        }
      }

      return () => {
        clearInterval($runningTimeRef.current);
        $runningTimeRef.current = undefined;
        $rightMenuFormikRef.current = undefined;
        $resizer.current = undefined;
      };
    },
  });

  useEffect(() => {
    const calculateInitialHeights = () => {
      const clientHeight = document.body.clientHeight;
      let multiplier = 0;
      if (clientHeight > 800 && clientHeight < 850) {
        multiplier = 0.04;
      } else if (clientHeight >= 850 && clientHeight < 880) {
        multiplier = 0.08;
      } else if (clientHeight >= 880 && clientHeight < 930) {
        multiplier = 0.12;
      } else if (clientHeight >= 930) {
        multiplier = 0.23;
      }

      const newAssistedModeHeight = clientHeight * 0.77;
      const newSqlEditorHeight = clientHeight * 0.376;
      const newAssistedModeBottom = clientHeight * multiplier * 0.7;
      const newSqlEditorBottom = clientHeight * 0.3 + clientHeight * 0.3 * multiplier;

      setTabsHeight({
        assistedModeHeight: newAssistedModeHeight,
        sqlEditorHeight: newSqlEditorHeight,
        assistedModeBottom: newAssistedModeBottom,
        sqlEditorBottom: newSqlEditorBottom,
      });
    };

    calculateInitialHeights();
  }, []);

  const handleResizerMove = useCallback(
    (e) => {
      const clientHeight = document.body.clientHeight;
      if (e.buttons === 1 && e.clientY >= clientHeight * 0.2 && e.clientY <= clientHeight * 0.85) {
        let multiplier = 0.1474;
        if (clientHeight >= 930) {
          multiplier = 0.13;
        }

        setTabsHeight((prev) => {
          const newHeight = e.clientY - 138;
          const newBottom = clientHeight - e.clientY - clientHeight * multiplier;
          return activeTopTabIndex === 0
            ? { ...prev, assistedModeHeight: newHeight, assistedModeBottom: newBottom }
            : { ...prev, sqlEditorHeight: newHeight, sqlEditorBottom: newBottom };
        });
      }
    },
    [activeTopTabIndex]
  );

  const handleMouseDown = useCallback(() => {
    window.addEventListener('mousemove', handleResizerMove);
  }, [handleResizerMove]);

  useEffect(() => {
    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleResizerMove);
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleResizerMove]);

  const startRunningTimer = () => {
    let time = 0;
    const executionTimer = () => {
      time += 1;
      const minutes = '' + Math.floor(time / 60);
      const seconds = '' + (time - minutes * 60);
      setRunningTime(`${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`);
    };
    executionTimer();
    $runningTimeRef.current = setInterval(executionTimer, 1000);
  };

  const stopRunningTimer = () => {
    clearInterval($runningTimeRef.current);
  };

  const validateFieldsConfiguration = useCallback((fieldsConfigs) => {
    return fieldsConfigs.find((f) => f.dimensionType === 'Measure');
  }, []);

  const formSchema = useMemo(() => {
    const structureNameValidator = async (structureName) => {
      try {
        if (structureId && structureToEdit.displayName === structureName) {
          return false;
        }
        if (structureName !== '') {
          const { alreadyUsed } = await Api.BimQuery.validateName({
            name: structureName,
            projectId: project.id,
          });
          return alreadyUsed;
        }
      } catch (e) {
        console.error('Error on BimQueryPAge.structureNameValidator()', { e });
        UiMsg.ajaxError(null, e);
        return true;
      }
    };
    return buildSchema(structureNameValidator, validateFieldsConfiguration, t);
  }, [structureToEdit, structureId]);

  const initialValues = useMemo(() => {
    const schemaDefaults = formSchema.default();
    if (!_.isEmpty(structureToEdit)) {
      schemaDefaults.sql = structureToEdit.sql;
      schemaDefaults.fieldConfigs = structureToEdit.fields;
      schemaDefaults.connection = structureToEdit.connectionId ?? 'BIMWarehouse';
      schemaDefaults.structureName = structureToEdit.structureName;
      schemaDefaults.displayName = structureToEdit.displayName;
      schemaDefaults.structureDescription = structureToEdit.structureDescription;
      schemaDefaults.structureIcon = structureToEdit.icon;
      schemaDefaults.structureType = structureToEdit.structureType;
      schemaDefaults.fullLoads = structureToEdit.fullLoads;
      schemaDefaults.structureTag = structureToEdit.groupId || 0;
      schemaDefaults.dataRecyclingEnabled = structureToEdit.dataRecyclingEnabled;
      schemaDefaults.dataRecyclingFieldName = structureToEdit.dataRecyclingFieldName || '';
      schemaDefaults.dataRecyclingPeriod = structureToEdit.dataRecyclingPeriod || 2;
      schemaDefaults.dataRecyclingType = structureToEdit.dataRecyclingType;
      schemaDefaults.dimensions = structureToEdit.dimensions;
      schemaDefaults.descriptionsCase = structureToEdit.descriptionsCase;
      schemaDefaults.descriptionsAccentuation = structureToEdit.descriptionsAccentuation;
      schemaDefaults.trim = structureToEdit.trim;
      schemaDefaults.trimWords = structureToEdit.trimWords;
      schemaDefaults.dataPartitioningEnabled = structureToEdit.dataPartitioningEnabled;
      schemaDefaults.dataPartitioningFieldName = structureToEdit.dataPartitioningFieldName;
      schemaDefaults.dataPartitioningRange = structureToEdit.dataPartitioningRange;
      schemaDefaults.bimQueryMetadata = _.cloneDeep(structureToEdit.bimQueryMetadata);
    }
    return schemaDefaults;
  }, [formSchema, structureToEdit]);
  return (
    <div className={`BimQueryPage ${styles.BimQueryPage}`}>
      <Formik
        enableReinitialize={true}
        validationSchema={formSchema}
        initialValues={initialValues}
        validateOnBlur={true}
        onSubmit={(values) => {
          alert(JSON.stringify(values, null, 2));
        }}
      >
        {(formikProps) => {
          const { values, setFieldValue, isValid, setValues } = formikProps;
          const [bottomTabIdx, setBottomTabIdx] = useState(0);

          useMemo(() => {
            if (_.isEmpty(validateFieldsConfiguration(values.fieldConfigs)) && values.fieldConfigs.length > 0) {
              UiMsg.error(t('inMemory_needAMeasure'));
            }
          }, [values.fieldConfigs]);

          const hasError = !_.isEmpty(queryExecutionLog);

          useEffect(() => {
            setBottomTabIdx(hasError ? 2 : 0);
          }, [hasError]);

          const unlockSideMenu = (_.isNil(queryExecutionLog) && values.fieldConfigs.length > 0) || structureId;
          const showFieldConfigs = !_.isEmpty(values.sql) && values.fieldConfigs.some((f) => f.visibleOnUi);

          const queryProps = {
            connectionId: values.connection === 'BIMWarehouse' ? 0 : values.connection,
            sql: values.sql,
            fromScheduler: false,
            projectId: project.id,
          };

          const validateQuery = async () => {
            if (_.isEmpty(values.sql)) {
              return;
            }

            try {
              setLoading(true);

              await Api.BimQuery.validateQuery(queryProps);
              UiMsg.ok(t('inmemory.sql.syntax.valid'));
              setQueryExecutionLog(null);
            } catch (e) {
              console.error('Error on validateQuery()', { e });
              setQueryExecutionLog(e.response.data.message);
              return false;
            } finally {
              setLoading(false);
            }
            return true;
          };

          const executeQuery = async () => {
            if (!(await validateQuery())) {
              return;
            }

            startRunningTimer();
            const clientHeight = document.body.clientHeight;
            setTabsHeight((prev) => ({
              ...prev,
              assistedModeHeight: clientHeight * 0.5,
              assistedModeBottom: clientHeight * 0.24,
            }));
            try {
              setLoading(true);
              const response = await Api.BimQuery.executeQuery({
                ...queryProps,
                pageSize: resultLimit,
              });

              setSourceSql(values.sql);
              setQueryResult(response.result);
              const normalizedFields = response.fields.map((f) => {
                f.description = normalizeFieldDescription(f.description);
                return f;
              });

              const fieldNodeMap = values.bimQueryMetadata.nodes.reduce((map, node) => {
                node.data.tableInfo.fields.forEach((col) => {
                  const desc = normalizeFieldDescription(col.name);
                  map[desc] = node.id;
                });
                return map;
              }, {});

              const updatedExistingFields = values.fieldConfigs.map((field) => ({
                ...field,
                visibleOnUi: false,
                nodeId: fieldNodeMap[field.fieldName] || '',
              }));

              const updatedFields = [
                ...updatedExistingFields.map((existingField) => {
                  const matchingNormalizedField = normalizedFields.find(
                    (normalizedField) => normalizedField.description === existingField.description
                  );

                  if (matchingNormalizedField) {
                    return {
                      ...existingField,
                      visibleOnUi: true,
                      nodeId: fieldNodeMap[existingField.fieldName] || '',
                    };
                  }

                  return existingField;
                }),
                ...normalizedFields
                  .filter(
                    (normalizedField) =>
                      !updatedExistingFields.some(
                        (existingField) => existingField.description === normalizedField.description
                      )
                  )
                  .map((newField) => ({
                    ...newField,
                    visibleOnUi: true,
                    nodeId: fieldNodeMap[newField.fieldName] || '',
                  })),
              ];

              setFieldValue('fieldConfigs', updatedFields);
              setQueryExecutionLog(null);
            } catch (e) {
              console.error('Error on executeQuery()', e);
              setQueryExecutionLog(e.response.data.message);
            } finally {
              setLoading(false);
              stopRunningTimer();
            }
          };

          const openAdaChat = (messageLogPreview) => {
            if (!checkAddonEnabled(AddonType.ADA_AI.key, false)) {
              UiMsg.warn(t('addon.adaai.not.hired'));
              return;
            }

            const chatData = {
              assistantKey: 'Assistant-BIMQuery-SQL',
              messageLogPreview,
              dashboardPath: '',
              itemPath: '',
              item: '',
              metadataObjectBimQuery: {
                dashboardItems: [{ id: 'Assistant-BIMQuery-SQL', name: 'BIMQuery' }],
              },
            };

            $adaAiChatStore.openChat(chatData);
          };

          const handleInsertNodeFromTableData = (tableData) => {
            const currentNodes = formikProps.values.bimQueryMetadata.nodes || [];
            const maxNodeX = currentNodes.length > 0 ? Math.max(...currentNodes.map((node) => node.position.x)) : 0;
            const newNodeX = maxNodeX + 400;
            const newNodeY = 0;

            const newNode = {
              id: UUID.generate(),
              type: 'databaseTable',
              position: { x: newNodeX, y: newNodeY },
              data: {
                tableInfo: tableData,
              },
            };

            setFieldValue('bimQueryMetadata.nodes', [...currentNodes, newNode]);

            const initialTableQuery = `SELECT ${tableData.fields.map((f) => `"${f.name}"`).join(', ')}
                                       FROM "${tableData.schema}"."${tableData.table}"`;

            const currentTableQueries = values.bimQueryMetadata.tableQueries || [];
            const updatedTableQueries = [
              ...currentTableQueries.filter((tq) => tq.id !== newNode.id),
              { id: newNode.id, query: initialTableQuery },
            ];

            setFieldValue('bimQueryMetadata.tableQueries', updatedTableQueries);
          };

          return (
            <UiBlocker block={loading} className={`${currentAccordion ? 'menu-opened' : 'menu-closed'}`}>
              <Form
                className={`${styles.FormWrapperStyle} ${currentAccordion ? 'menu-opened' : 'menu-closed'} ${
                  loading ? 'is-loading' : ''
                }`}
              >
                <div className={`${styles.FloatActionButton} ${currentAccordion ? 'menu-opened' : 'menu-closed'}`}>
                  {activeTopTabIndex === 1 &&
                    !blockEdit &&
                    (values.bimQueryMetadata.mode === 'SQL_MODE' ? (
                      <BngIconButton
                        text={t('bim.query.tab.clear.sql.button.label')}
                        className={`${styles.ClearSQLEditor}`}
                        icon="cleaning_services"
                        disabled={!values.sql}
                        onClick={() => {
                          setValues({
                            ...values,
                            sql: '',
                            fieldConfigs: [],
                            bimQueryMetadata: {
                              ...values.bimQueryMetadata,
                              nodes: [],
                            },
                          });
                          setQueryResult([]);
                        }}
                      />
                    ) : (
                      <BngIconButton
                        text={t('bim.query.enable.sql.mode')}
                        className={`${styles.EnableModesButton}`}
                        icon="edit"
                        onClick={() => {
                          OpConfirmation({
                            title: t('attention'),
                            html: t('bim.query.enable.sql.mode.confirmation'),
                            onConfirm: () => {
                              setValues({
                                ...values,
                                bimQueryMetadata: {
                                  ...values.bimQueryMetadata,
                                  mode: 'SQL_MODE',
                                },
                              });
                            },
                          });
                        }}
                      />
                    ))}

                  {activeTopTabIndex === 0 && !blockEdit && values.bimQueryMetadata.mode === 'SQL_MODE' && (
                    <BngIconButton
                      text={t('bim.query.enable.assisted.mode')}
                      className={`${styles.EnableModesButton}`}
                      icon="edit"
                      onClick={() => {
                        OpConfirmation({
                          title: t('attention'),
                          html: t('bim.query.enable.assisted.mode.confirmation'),
                          onConfirm: () => {
                            setValues({
                              ...values,
                              sql: values.sql,
                              bimQueryMetadata: {
                                ...values.bimQueryMetadata,
                                mode: 'ASSISTED_MODE',
                              },
                            });
                          },
                        });
                      }}
                    />
                  )}

                  <BngIconButton
                    icon={<img src={Api.buildUrl('/resources/images/ada-ai/ada-ai-icon.png')} alt="Ada AI" />}
                    className="ada-ai"
                    text={msg.t('sql.ada.ia.query.assistant.button')}
                    onClick={() => openAdaChat()}
                  />

                  {Utils.Project.isBimachineEmail(user.email) && (
                    <BngIconButton
                      icon="data_object"
                      text={t('export.json')}
                      className={`${styles.ExportJsonButton}`}
                      onClick={() => {
                        const jsonStr = JSON.stringify(values.bimQueryMetadata, null, 2);

                        const blob = new Blob([jsonStr], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        try {
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `bimQueryMetadata_${Date.now()}.json`;
                          link.click();
                        } finally {
                          URL.revokeObjectURL(url);
                        }
                      }}
                    />
                  )}
                </div>
                <TabSet
                  internal={true}
                  tabToOpenIndex={activeTopTabIndex}
                  selectedTab={activeTopTabIndex}
                  handleTabChange={(tabIdx) => setActiveTopTabIndex(tabIdx)}
                  className={`${styles.TopTabsWrapper}`}
                >
                  <Tab icon="outbound" label={t('bim.query.tab.title.assisted.mode')}>
                    {(blockEdit || values.bimQueryMetadata.mode === 'SQL_MODE') && activeTopTabIndex === 0 && (
                      <div className={`${styles.AreaBlocker}`}>
                        {blockEdit && (
                          <BngIconButton
                            icon="edit"
                            text={t('bim.query.enable.edit')}
                            className={`${styles.EnableEditButton}`}
                            onClick={() => {
                              OpConfirmation({
                                title: t('attention'),
                                html: t('in_memory_dirty_confirmation'),
                                onConfirm: () => {
                                  setBlockEdit(false);
                                },
                              });
                            }}
                          />
                        )}
                      </div>
                    )}
                    <AssistedMode
                      nodes={values.bimQueryMetadata.nodes || []}
                      edges={values.bimQueryMetadata.edges || []}
                      formikProps={formikProps}
                      height={tabsHeight.assistedModeHeight}
                    />
                    <Icon className={`${styles.resizeIcon}`} icon="drag_handle" onMouseDown={handleMouseDown} />
                  </Tab>
                  <Tab icon="code" label={t('bim.query.tab.title.sql.mode')} className={styles.SqlModeTab}>
                    {(blockEdit || values.bimQueryMetadata.mode === 'ASSISTED_MODE') && activeTopTabIndex === 1 && (
                      <div className={`${styles.AreaBlocker}`}>
                        {blockEdit && (
                          <BngIconButton
                            icon="edit"
                            text={t('bim.query.enable.edit')}
                            className={`${styles.EnableEditButton}`}
                            onClick={() => {
                              OpConfirmation({
                                title: t('attention'),
                                html: t('in_memory_dirty_confirmation'),
                                onConfirm: () => {
                                  setBlockEdit(false);
                                },
                              });
                            }}
                          />
                        )}
                      </div>
                    )}
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        const data = e.dataTransfer.getData('application/reactflow');
                        if (data) {
                          const tableData = JSON.parse(data);
                          handleInsertNodeFromTableData(tableData);
                        }
                      }}
                    >
                      <BimQuerySqlEditor
                        style={{ height: `${tabsHeight.sqlEditorHeight}px` }}
                        structureId={structureId}
                        readOnly={blockEdit || values.bimQueryMetadata.mode === 'ASSISTED_MODE'}
                      />
                    </div>
                    <BngIconButton
                      icon="content_copy"
                      className={`${styles.CopySqlButton}`}
                      onClick={() => {
                        Utils.copyToClipboard(values.sql);
                        UiMsg.ok(msg.t('query.copied.to.clipboard'));
                      }}
                    />
                    <Icon className={`${styles.resizeIcon}`} icon="drag_handle" onMouseDown={handleMouseDown} />
                  </Tab>
                </TabSet>
                <div className={`${styles.CenterButtonsWrapper}`}>
                  <div className={`${styles.ActionsButtons} ${currentAccordion ? 'menu-opened' : 'menu-closed'}`}>
                    <BngDropdown
                      popperClassName={`${styles.QueryResultLinesAmountPopper} `}
                      customButton={({ openDropdown }) => (
                        <div className={`${styles.ExecuteQueryButton}`}>
                          <div onClick={executeQuery}>
                            <Icon icon="play_circle" />
                            {t('execute')}
                          </div>
                          <Icon
                            icon="arrow_drop_down"
                            onClick={(event) => {
                              openDropdown(event);
                            }}
                          />
                        </div>
                      )}
                      options={[20, 50, 100, 1000].map((limit) => ({
                        className: styles.queryResultLinesOption,
                        label: `${limit} ${t('bim.query.dropdown.line.selector.option')}`,
                        icon: `${resultLimit === limit ? 'done' : ''}`,
                        onClick: () => setResultLimit(limit),
                      }))}
                    />
                    <div className={`${styles.Timer}`}>{runningTime}</div>
                  </div>
                  <div className={`${styles.OptionsLeftSide}`}>
                    {bottomTabIdx === 1 && values.fieldConfigs.length > 0 && (
                      <div
                        className={`${styles.ExportViewButton}`}
                        onClick={() => {
                          bimEventBus.emit(QueryResultPreview.EXPORT_CSV_EVENT);
                        }}
                      >
                        <div className={`${styles.csvIconStyle}`}>csv</div>
                        {t('bim.query.export.preview.button.label')}
                      </div>
                    )}
                  </div>
                </div>
                <TabSet
                  internal={true}
                  tabToOpenIndex={0}
                  selectedTab={bottomTabIdx}
                  handleTabChange={(tabIdx) => setBottomTabIdx(tabIdx)}
                  className={`${styles.TabSetWrapper}`}
                >
                  <Tab
                    icon="settings"
                    label={t('configuration')}
                    className={`${styles.ColumnsConfigurationTab}`}
                    style={{
                      height: activeTopTabIndex === 0 ? tabsHeight.assistedModeBottom : tabsHeight.sqlEditorBottom,
                    }}
                  >
                    {showFieldConfigs ? (
                      <ColumnsConfiguration blockEdit={blockEdit} />
                    ) : (
                      <div className={`${styles.EmptyConfigurationTabLabel}`}>
                        {t('bim.query.configurations.empty')}
                      </div>
                    )}
                  </Tab>
                  <Tab
                    icon="visibility"
                    label={t('preview')}
                    disabled={_.isNil(queryResult)}
                    className={`${styles.QueryResultPreviewTab}`}
                    style={{
                      height: activeTopTabIndex === 0 ? tabsHeight.assistedModeBottom : tabsHeight.sqlEditorBottom,
                    }}
                  >
                    {_.isEmpty(queryResult) ? (
                      <div className={`${styles.EmptyConfigurationTabLabel}`}>{t('bim.query.preview.empty')}</div>
                    ) : (
                      <QueryResultPreview queryResult={queryResult} fieldConfigs={values.fieldConfigs} />
                    )}
                  </Tab>
                  <Tab
                    icon="description"
                    label={t('log')}
                    disabled={!hasError}
                    className={`${styles.LogPreviewTab}`}
                    props={{ alert: hasError }}
                    tabClassName={`${styles.LogPreviewTabButton}`}
                    style={{
                      height: activeTopTabIndex === 0 ? tabsHeight.assistedModeBottom : tabsHeight.sqlEditorBottom,
                    }}
                  >
                    {hasError && (
                      <LogPreview
                        messageLog={queryExecutionLog}
                        openAdaChat={() => {
                          const connection = $pageCtx.connections.find((c) => c.id === values.connection);
                          const connectionName = connection?.type?.name || 'PostgreSQL';
                          openAdaChat({
                            message: queryExecutionLog,
                            messageGpt: `${queryExecutionLog} ${connectionName} ${values.sql}`,
                            user: {
                              displayName: Utils.Users.displayName(user),
                              avatar: user.avatarLink,
                            },
                          });
                        }}
                      />
                    )}
                  </Tab>
                </TabSet>
                <div className={`BimQueryRightMenuWrapper ${styles.BimQueryRightMenuWrapper}`}>
                  <BimQueryRightMenu
                    structureId={structureId}
                    formikProps={formikProps}
                    onAccordionChange={({ accordionKey }) => {
                      setCurrentAccordion(accordionKey);
                    }}
                    onFormikRef={$rightMenuFormikRef}
                    queryExecutionLog={queryExecutionLog}
                    isEditing={!!structureId}
                    unlockSideMenu={unlockSideMenu}
                    saveDisabled={unlockSideMenu ? !isValid : true}
                    blockEdit={blockEdit}
                    loading={loading}
                    setLoading={setLoading}
                    sourceSql={sourceSql}
                    handleInsertNode={handleInsertNodeFromTableData}
                  />
                </div>
              </Form>
            </UiBlocker>
          );
        }}
      </Formik>
    </div>
  );
}
